import { useEffect, useRef, useState } from "react";
import { Image as ImageIcon, PenLine, Send, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import DrawingCanvas from "./DrawingCanvas";
import { toast } from "sonner";

const MAX_IMAGE_BYTES = 1.4 * 1024 * 1024; // ~1.4MB raw → ok for base64 in MongoDB

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

export default function Composer({ onCreate, prefill }) {
  const [text, setText] = useState("");
  const [drawing, setDrawing] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const taRef = useRef(null);

  // Apply external prefill (e.g. from Daily Prompt) — focus + grow
  useEffect(() => {
    if (prefill) {
      setText((prev) => (prev ? prev : prefill));
      requestAnimationFrame(() => {
        const ta = taRef.current;
        if (ta) {
          ta.focus();
          ta.style.height = "auto";
          ta.style.height = Math.min(ta.scrollHeight, 220) + "px";
          // Place caret at end
          const len = ta.value.length;
          ta.setSelectionRange(len, len);
        }
      });
    }
  }, [prefill]);

  const handleTextChange = (e) => {
    setText(e.target.value);
    const ta = taRef.current;
    if (ta) {
      ta.style.height = "auto";
      ta.style.height = Math.min(ta.scrollHeight, 220) + "px";
    }
  };

  async function pickImage(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > MAX_IMAGE_BYTES) {
      toast.error("image too large — keep it under 1.4MB for now");
      return;
    }
    const data = await fileToBase64(file);
    setImagePreview(data);
  }

  async function submitText() {
    const t = text.trim();
    if (!t || busy) return;
    setBusy(true);
    const ok = await onCreate({ type: "text", content: t });
    setBusy(false);
    if (ok) {
      setText("");
      if (taRef.current) taRef.current.style.height = "auto";
    }
  }

  async function submitImage() {
    if (!imagePreview || busy) return;
    setBusy(true);
    const ok = await onCreate({ type: "image", content: imagePreview, caption: text.trim() || null });
    setBusy(false);
    if (ok) {
      setImagePreview(null);
      setText("");
    }
  }

  async function submitDrawing(dataUrl) {
    setDrawing(false);
    setBusy(true);
    await onCreate({ type: "drawing", content: dataUrl });
    setBusy(false);
  }

  const sendDisabled = busy || (!text.trim() && !imagePreview);

  return (
    <div
      className="fixed bottom-16 sm:bottom-5 left-1/2 -translate-x-1/2 w-[calc(100%-1.5rem)] max-w-2xl z-40"
      data-testid="composer-root"
    >
      <AnimatePresence>
        {drawing && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            className="mb-3"
          >
            <DrawingCanvas onCancel={() => setDrawing(false)} onSave={submitDrawing} />
          </motion.div>
        )}
      </AnimatePresence>

      <div className="glass-surface rounded-2xl shadow-lg shadow-black/10">
        {imagePreview && (
          <div className="relative p-3 border-b border-border" data-testid="image-preview">
            <img
              src={imagePreview}
              alt="preview"
              className="rounded-lg max-h-48 w-auto mx-auto"
            />
            <button
              onClick={() => setImagePreview(null)}
              className="absolute top-4 right-4 bg-background/80 backdrop-blur rounded-full p-1.5 hover:bg-secondary"
              aria-label="remove image"
              data-testid="composer-remove-image-button"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        )}

        <div className="flex items-end gap-2 px-3 py-2.5">
          <div className="flex items-center gap-1">
            <button
              onClick={() => fileRef.current?.click()}
              className="p-2 rounded-md hover:bg-secondary transition-colors"
              aria-label="add image"
              data-testid="composer-image-button"
            >
              <ImageIcon className="w-4 h-4" />
            </button>
            <button
              onClick={() => setDrawing(true)}
              className="p-2 rounded-md hover:bg-secondary transition-colors"
              aria-label="draw"
              data-testid="composer-draw-button"
            >
              <PenLine className="w-4 h-4" />
            </button>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={pickImage}
              data-testid="composer-image-input"
            />
          </div>

          <textarea
            ref={taRef}
            rows={1}
            placeholder={imagePreview ? "add a caption…" : "what's on your mind…"}
            value={text}
            onChange={handleTextChange}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey && !imagePreview) {
                e.preventDefault();
                submitText();
              }
            }}
            className="flex-1 resize-none bg-transparent composer-textarea font-serif-journal text-base leading-relaxed py-1.5 px-1 max-h-56"
            data-testid="composer-textarea"
          />

          <button
            onClick={imagePreview ? submitImage : submitText}
            disabled={sendDisabled}
            className="p-2 rounded-md bg-primary text-primary-foreground disabled:opacity-30 hover:opacity-90 transition-opacity"
            aria-label="send"
            data-testid="composer-send-button"
          >
            <Send className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
